import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAdsOptimizationConfig,
  runAdsOptimizationNow,
  updateAdsOptimizationConfig,
  type AdsOptimizationConfigDto,
  type AdsOptimizationConfigPatch,
} from "../../../helpers/api/ads-optimization";

interface Props {
  customerId: string;
}

const DEFAULT_DRY_RUN_DAYS = 15;

// ---------------------------------------------------------------------------
// Status derivation. The four states are mutually exclusive and computed from
// the same three backend fields (enabled, killSwitch, dryRunUntil) that the
// runner uses, so UI and backend can't disagree.
// ---------------------------------------------------------------------------

type Status = "off" | "dry_run" | "live" | "paused";

interface StatusInfo {
  status: Status;
  badgeLabel: string;
  badgeClass: string;
  badgeTooltip: string;
  headline: string;
  detail: string;
  daysLeft?: number;
}

function deriveStatus(cfg: AdsOptimizationConfigDto): StatusInfo {
  if (!cfg.enabled) {
    return {
      status: "off",
      badgeLabel: "Off",
      badgeClass: "bg-gray-100 text-gray-700",
      badgeTooltip:
        "Agent is not running for this customer. No evaluation, no logs, no changes to Google Ads.",
      headline: "Agent is off for this customer",
      detail: "No daily evaluation runs. Nothing is logged.",
    };
  }
  if (cfg.killSwitch) {
    return {
      status: "paused",
      badgeLabel: "Paused",
      badgeClass: "bg-rose-50 text-rose-700",
      badgeTooltip:
        "Kill switch is on. The daily cron skips this customer entirely. No changes to Google Ads until you click Resume.",
      headline: "Agent is paused (emergency stop)",
      detail:
        "Kill switch is on. The daily run will skip this customer entirely. Use this when something is going wrong and you need to halt budget changes immediately.",
    };
  }
  const isDryRun = cfg.dryRunUntil && new Date(cfg.dryRunUntil) > new Date();
  if (isDryRun) {
    const daysLeft = Math.ceil(
      (new Date(cfg.dryRunUntil!).getTime() - Date.now()) /
        (24 * 60 * 60 * 1000),
    );
    return {
      status: "dry_run",
      badgeLabel: "Dry-run",
      badgeClass: "bg-blue-50 text-blue-700",
      badgeTooltip:
        "Agent evaluates all 4 rules daily and writes decisions to the log, but does NOT touch Google Ads. Use this period to verify the decisions look sensible before going Live.",
      headline: `Dry-run · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`,
      detail:
        "Agent evaluates all rules daily and logs decisions below, but does NOT touch Google Ads. Review the log; switch to Live when you're confident the decisions look right.",
      daysLeft,
    };
  }
  return {
    status: "live",
    badgeLabel: "Live",
    badgeClass: "bg-emerald-50 text-emerald-700",
    badgeTooltip:
      "Agent runs daily and applies real budget and bid changes to this customer's Google Ads account. Every change is recorded in the decision log.",
    headline: "Live: agent is changing this customer's Google Ads",
    detail:
      "Daily evaluation applies real budget and bid changes to Google Ads. Every change is recorded in the decision log below.",
  };
}

// ---------------------------------------------------------------------------
// Modifiers, described by effect, not by rule code. Ops shouldn't need to
// remember which rule does what to use these.
// ---------------------------------------------------------------------------

interface ModifierDef {
  key: "notOnOtas" | "bpComplaints" | "allowBpDominance" | "excludeFromReallocation";
  label: string;
  effect: string;
}

const MODIFIERS: ModifierDef[] = [
  {
    key: "notOnOtas",
    label: "Hotel is not listed on OTAs (Booking, Expedia, etc.)",
    effect:
      "Agent becomes more aggressive about pulling back brand-protection spend, since the customer isn't competing for brand traffic with OTAs.",
  },
  {
    key: "bpComplaints",
    label: "Customer has complained about brand-protection spend",
    effect:
      "Agent becomes the most aggressive about reducing brand-protection budgets. Use after a CS escalation.",
  },
  {
    key: "allowBpDominance",
    label: "Never reduce brand-protection budgets for this customer",
    effect:
      "Use for hotels that explicitly want maximum brand-protection coverage regardless of share (e.g. Brass Lantern, Metropole).",
  },
  {
    key: "excludeFromReallocation",
    label: "Don't park zero-conversion campaigns",
    effect:
      "Agent will leave campaigns with no bookings alone instead of parking them at $0.01/day. Use for hotels that need exposure variety.",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdsOptimizationSection({ customerId }: Props) {
  const qc = useQueryClient();
  const queryKey = ["ads-optimization", "config", customerId];

  const { data, isLoading } = useQuery<AdsOptimizationConfigDto>({
    queryKey,
    queryFn: () => fetchAdsOptimizationConfig(customerId),
  });

  // Local form state. Only modifiers and budget cap are user-editable here.
  // Status transitions go through dedicated action buttons.
  const [dailyBudgetCap, setDailyBudgetCap] = useState("4");
  const [modifiers, setModifiers] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);

  useEffect(() => {
    if (data && !dirty) {
      setDailyBudgetCap(String(data.dailyBudgetCap));
      setModifiers({
        notOnOtas: data.notOnOtas,
        bpComplaints: data.bpComplaints,
        allowBpDominance: data.allowBpDominance,
        excludeFromReallocation: data.excludeFromReallocation,
      });
    }
  }, [data, dirty]);

  const patch = useMutation({
    mutationFn: (p: AdsOptimizationConfigPatch) =>
      updateAdsOptimizationConfig(customerId, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({
        queryKey: ["ads-optimization", "config-changes", customerId],
      });
      setDirty(false);
    },
  });

  const runNow = useMutation({
    mutationFn: () => runAdsOptimizationNow(customerId),
    onSuccess: (r) => {
      setRunResult(
        `Run finished: ${r.status}, ${r.decisionsCount} decision${r.decisionsCount === 1 ? "" : "s"}${r.mode ? ` (${r.mode === "dry_run" ? "dry-run" : "live"})` : ""}${r.reason ? ` · ${r.reason}` : ""}`,
      );
      qc.invalidateQueries({
        queryKey: ["ads-optimization", "decisions", customerId],
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      setRunResult(`Run failed: ${msg}`);
    },
  });

  if (isLoading || !data) {
    return (
      <section className="bg-white rounded-xl border border-border-secondary shadow-sm p-6 h-32 animate-pulse" />
    );
  }

  const info = deriveStatus(data);

  // Available actions depend on current status (mutually exclusive transitions),
  // so the user can never save a contradictory state.
  const actions: Array<{
    label: string;
    intent: "primary" | "danger" | "secondary";
    patch: AdsOptimizationConfigPatch;
    confirm?: string;
    info?: string;
  }> = [];

  if (info.status === "off") {
    actions.push({
      label: `Start ${DEFAULT_DRY_RUN_DAYS}-day dry-run`,
      intent: "primary",
      patch: { startDryRunDays: DEFAULT_DRY_RUN_DAYS },
      info:
        "This is step 1 of 2. For the next 15 days the agent runs daily and writes its decisions to the log, but does NOT make real changes to Google Ads. After you have reviewed the log and the decisions look right, come back here and click 'Switch to live' (step 2) to start applying real changes. If you do nothing, the agent will auto-flip to Live when the 15-day window expires.",
    });
  }
  if (info.status === "dry_run") {
    actions.push({
      label: "Switch to live",
      intent: "primary",
      // dryRunUntil: null clears the dry-run window. Without this, status
      // stays "dry-run" until the timer expires and the button does nothing
      // visible.
      patch: { enabled: true, killSwitch: false, dryRunUntil: null },
      confirm:
        "Switch to LIVE? The agent will start making real budget and bid changes on this customer's Google Ads on the next daily run.",
      info:
        "Step 2 of 2. The agent starts applying real budget and bid changes to this customer's Google Ads account on the next 06:00 UTC run.",
    });
    actions.push({
      label: "Pause",
      intent: "danger",
      patch: { killSwitch: true },
    });
    actions.push({
      label: "Turn off",
      intent: "secondary",
      patch: { enabled: false, killSwitch: false },
    });
  }
  if (info.status === "live") {
    actions.push({
      label: "Pause",
      intent: "danger",
      patch: { killSwitch: true },
    });
    actions.push({
      label: "Turn off",
      intent: "secondary",
      patch: { enabled: false, killSwitch: false },
    });
  }
  if (info.status === "paused") {
    actions.push({
      label: "Resume",
      intent: "primary",
      patch: { killSwitch: false },
    });
    actions.push({
      label: "Turn off",
      intent: "secondary",
      patch: { enabled: false, killSwitch: false },
    });
  }

  const handleAction = (a: (typeof actions)[number]) => {
    if (a.confirm && !window.confirm(a.confirm)) return;
    patch.mutate(a.patch);
  };

  const handleSaveSettings = () => {
    patch.mutate({
      dailyBudgetCap: Number(dailyBudgetCap) || 0,
      notOnOtas: !!modifiers.notOnOtas,
      bpComplaints: !!modifiers.bpComplaints,
      allowBpDominance: !!modifiers.allowBpDominance,
      excludeFromReallocation: !!modifiers.excludeFromReallocation,
    });
  };

  const canRunNow = info.status === "dry_run" || info.status === "live";

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <header className="px-5 py-4 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Ads Optimization Agent
        </h2>
        <p className="text-[11px] text-content-tertiary leading-snug mt-0.5 max-w-2xl">
          Runs automatically every day at 06:00 UTC. Adjusts Google Ads
          campaign budgets and keyword bids using 4 fixed rules (see "Rules"
          below). All decisions are recorded in the log below.
        </p>
      </header>

      <div className="p-5 flex flex-col gap-5">
        {/* Status card */}
        <div
          className={clsx(
            "rounded-lg border p-4 flex flex-col gap-3",
            info.status === "live" && "border-emerald-200 bg-emerald-50/40",
            info.status === "dry_run" && "border-blue-200 bg-blue-50/40",
            info.status === "paused" && "border-rose-200 bg-rose-50/40",
            info.status === "off" && "border-border-secondary bg-gray-50/40",
          )}
        >
          <div className="flex items-center gap-2">
            <span
              title={info.badgeTooltip}
              className={clsx(
                "text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded cursor-help",
                info.badgeClass,
              )}
            >
              {info.badgeLabel}
            </span>
            <span className="text-sm font-medium text-content-primary">
              {info.headline}
            </span>
          </div>
          <p className="text-xs text-content-secondary leading-relaxed">
            {info.detail}
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {actions.map((a) => (
              <div key={a.label} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleAction(a)}
                  disabled={patch.isPending}
                  className={clsx(
                    "px-3 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-50",
                    a.intent === "primary" &&
                      "bg-blue-600 text-white hover:bg-blue-700",
                    a.intent === "danger" &&
                      "bg-white border border-rose-300 text-rose-700 hover:bg-rose-50",
                    a.intent === "secondary" &&
                      "bg-white border border-border-secondary text-content-secondary hover:bg-gray-50",
                  )}
                >
                  {a.label}
                </button>
                {a.info && <InfoTooltip text={a.info} />}
              </div>
            ))}
            {canRunNow && (
              <div className="flex items-center gap-1 ml-auto">
                <button
                  type="button"
                  onClick={() => runNow.mutate()}
                  disabled={runNow.isPending}
                  className="text-xs font-medium text-blue-700 hover:underline disabled:text-content-tertiary disabled:no-underline disabled:cursor-not-allowed"
                >
                  {runNow.isPending ? "Running…" : "Run now"}
                </button>
                <InfoTooltip text="Optional. Triggers an evaluation right now instead of waiting for the next 06:00 UTC cron. The agent runs automatically every day. You do NOT need to click this button for the agent to work." />
              </div>
            )}
          </div>
        </div>

        {runResult && (
          <div className="text-xs text-content-secondary bg-gray-50 border border-border-secondary rounded px-3 py-2">
            {runResult}
          </div>
        )}

        <RulesBlock />

        {/* Settings only matter when the agent is doing something. */}
        {info.status !== "off" && (
          <>
            <SettingsBlock title="Daily budget cap">
              <div className="flex items-center gap-3">
                <span className="text-xs text-content-tertiary">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={dailyBudgetCap}
                  onChange={(e) => {
                    setDailyBudgetCap(e.target.value);
                    setDirty(true);
                  }}
                  className="w-24 px-2 py-1 border border-border-secondary rounded text-sm"
                />
                <span className="text-xs text-content-tertiary">/ day</span>
              </div>
              <p className="text-[11px] text-content-tertiary leading-snug">
                The agent will never raise any single campaign's daily budget
                above this number. Recommended: about 2.5% of the hotel's ADR,
                capped at $4/day.
              </p>
            </SettingsBlock>

            <SettingsBlock title="Optional overrides">
              <p className="text-[11px] text-content-tertiary leading-snug">
                Leave all unchecked for the standard behavior. That is the
                right answer for most hotels. Tick a box only when this
                specific hotel has a known special situation (BP complaints,
                no OTA presence, etc.).
              </p>
              <div className="flex flex-col gap-3 mt-2">
                {MODIFIERS.map((m) => (
                  <ModifierRow
                    key={m.key}
                    label={m.label}
                    effect={m.effect}
                    checked={!!modifiers[m.key]}
                    onChange={(v) => {
                      setModifiers({ ...modifiers, [m.key]: v });
                      setDirty(true);
                    }}
                  />
                ))}
              </div>
            </SettingsBlock>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={!dirty || patch.isPending}
                className={clsx(
                  "px-4 py-1.5 rounded text-xs font-semibold transition-colors",
                  dirty && !patch.isPending
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-100 text-content-tertiary cursor-not-allowed",
                )}
              >
                {patch.isPending ? "Saving…" : "Save budget & overrides"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function SettingsBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 pt-4 border-t border-border-secondary first:border-t-0 first:pt-0">
      <h3 className="text-xs font-semibold text-content-primary uppercase tracking-wider">
        {title}
      </h3>
      {children}
    </div>
  );
}

interface ModifierRowProps {
  label: string;
  effect: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

// Standard outlined info icon (Lucide-style). Lives next to action buttons
// and the Run now control. Native title attribute carries the tooltip text,
// which is the same string screen readers get via aria-label.
function InfoTooltip({ text }: { text: string }) {
  return (
    <span
      title={text}
      aria-label={text}
      role="img"
      className="inline-flex text-content-tertiary hover:text-blue-600 cursor-help transition-colors"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-3.5 h-3.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    </span>
  );
}

function ModifierRow({ label, effect, checked, onChange }: ModifierRowProps) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-border-secondary text-blue-600 focus:ring-blue-500"
      />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-content-primary group-hover:text-content-primary">
          {label}
        </span>
        <span className="text-[11px] text-content-tertiary leading-snug">
          → {effect}
        </span>
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// "Rules": the 4 things the agent does, in plain English. Collapsed by default
// so it doesn't clutter the section on repeat visits.
// ---------------------------------------------------------------------------

const RULES: Array<{
  title: string;
  trigger: string;
  action: string;
}> = [
  {
    title: "Pull back Brand Protection when it's cannibalizing organic",
    trigger:
      "BP campaigns made over $10K in bookings in the last 30 days AND account for more than 80% of all bookings.",
    action: "Reduce BP daily budget by 20%.",
  },
  {
    title: "Scale non-brand campaigns that are converting",
    trigger:
      "Any non-BP campaign got at least 1 conversion in the last 30 days. First-ever conversion on the campaign bumps the boost from +25% to +50%.",
    action: "Raise that campaign's daily budget by +25%.",
  },
  {
    title: "Park dead campaigns and feed winners",
    trigger:
      "Campaign has more than $2/day average spend with 0 conversions in the last 90 days.",
    action:
      "Drop daily budget to $0.01/day. Freed budget gets reallocated proportionally to campaigns being scaled by rule 2.",
  },
  {
    title: "Lower CPC on losing keywords (and pause the worst)",
    trigger:
      "Keyword spent at least $100 in the last 14 days with ROAS below 1.0x.",
    action:
      "Lower max CPC bid by 20%. Pause the keyword entirely if it keeps failing for 14 consecutive days.",
  },
];

function RulesBlock() {
  return (
    <details className="group bg-gray-50/60 border border-border-secondary rounded-lg open:bg-white">
      <summary className="px-4 py-2.5 cursor-pointer select-none flex items-center gap-2 hover:bg-gray-50/80 transition-colors">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-3.5 h-3.5 text-content-tertiary transition-transform group-open:rotate-90"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span className="text-xs font-semibold text-content-primary">
          Rules
        </span>
        <span className="text-[11px] text-content-tertiary">
          The 4 things this agent does
        </span>
      </summary>
      <div className="px-4 pb-4 pt-1">
        <ol className="flex flex-col gap-3">
          {RULES.map((r, idx) => (
            <li
              key={r.title}
              className="bg-white border border-border-secondary rounded-md px-3 py-2.5 flex gap-3"
            >
              <span className="flex-none w-5 h-5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-content-primary">
                  {r.title}
                </span>
                <span className="text-[11px] text-content-secondary leading-snug">
                  <strong className="text-content-tertiary font-medium">
                    When:
                  </strong>{" "}
                  {r.trigger}
                </span>
                <span className="text-[11px] text-content-secondary leading-snug">
                  <strong className="text-content-tertiary font-medium">
                    Action:
                  </strong>{" "}
                  {r.action}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}
